import { useReviewPaymentRequest } from '@/apis/payments/hooks/usePostPaymentDecision'
import { useAppAlert } from '@/app/AppAlertProvider'
import { Button } from '@/app/common/components/Button'
import { Card, CardContent } from '@/app/common/components/Card'
import FormErrorMessage from '@/app/common/components/FormErrorMessage'
import { PaymentSummary } from '@/app/common/components/purse_allocation_form/payment_summary'
import { PurseAllocationForm } from '@/app/common/components/purse_allocation_form/purse_allocation_form'
import { PurseAllocationSummary } from '@/app/common/components/purse_allocation_form/purse_allocation_summary'
import usePurseAllocationForm from '@/app/common/components/purse_allocation_form/usePurseAllocationForm'
import { CheckCircleIcon } from '@/app/common/icons/arrows'
import { FailCircleIcon } from '@/app/common/icons/general'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button'
import { PaymentDetail } from '@/schemas/payments.schema'
import { PursesArray } from '@/schemas/provider.schema'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog'
import { Textarea } from '@/shad/ui/textarea'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { createSendBillFormSchema } from './accept_payment.schema'
import { getTotalAmountToBePaidByApplicant } from '@/app/common/payment.utils'

type Props = {
  payment: PaymentDetail
  purses: PursesArray
  onSuccess: () => void
}

export function ReviewSendBillOrder(props: Props) {
  const { payment, purses, onSuccess } = props
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)
  const { t } = useTranslation()
  const { fireAlert } = useAppAlert()

  const totalPayment = getTotalAmountToBePaidByApplicant({
    base_amount: payment.amount_provider,
    net_amount: payment.total_amount,
    serviceFeePayer: payment.service_fee_payer,
    service_fee: payment.service_fee,
  })

  const acceptFormMethods = usePurseAllocationForm({
    apiPurses: purses,
    baseAmount: payment.amount_provider,
    serviceFee: payment.service_fee,
    serviceFeePayer: payment.service_fee_payer,
    totalAmount: payment.total_amount,
    selectedPurses: [],
    customSchema: createSendBillFormSchema(
      purses,
      totalPayment,
      payment.allowed_students || []
    ),
  })
  const rejectFormMethods = useForm({})

  const acceptMutation = useReviewPaymentRequest({
    onSuccess: (response) => {
      const processedImmediately = response.status === 'Approved'
      fireAlert({
        variant: 'success',
        title: true,
        titleText: 'Payment Accepted',
        message: t(
          processedImmediately
            ? 'payment_decision_success.processed_immediately'
            : 'payment_decision_success'
        ),
      })
      onSuccess()
    },
    onError: (error) => {
      fireAlert({
        variant: 'error',
        message: error.message,
        title: false,
      })
    },
  })

  const rejectMutation = useReviewPaymentRequest({
    onSuccess: () => {
      fireAlert({
        message: t('payment_rejected_success'),
        variant: 'error',
        titleText: 'Payment Declined',
        title: true,
      })
      onSuccess()
    },
    onError: (error) => {
      fireAlert({
        message: error.message,
        variant: 'error',
      })
    },
  })

  function handleAcceptPayment() {
    acceptMutation.mutate({
      comment: '',
      decision: 'accept',
      payment_id: payment.payment_id,
      program_id: payment.program_id,
      purse_payments: acceptFormMethods.getSelectedPurses(),
    })
  }

  function handleRejectPayment() {
    rejectMutation.mutate({
      comment: rejectFormMethods.getValues('comment'),
      decision: 'reject',
      payment_id: payment.payment_id,
      program_id: payment.program_id,
      purse_payments: [],
    })
  }

  const { totalApplicantAmount } = acceptFormMethods
  return (
    <div className="mb-4 w-full">
      <Card className="w-full">
        <CardContent className="flex items-center justify-between">
          <div>
            <div className="font-medium">{t('review_payment')}</div>
            <div></div>
          </div>
          <div className="space-x-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button className="" variant={'outline'} onClick={() => {}}>
                  <CheckCircleIcon className="text-emerald-600 mr-1" />
                  {t('accept_payment')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-screen overflow-y-auto">
                <PaymentSummary
                  paymentAmount={totalApplicantAmount}
                  serviceFeePayer={payment.service_fee_payer}
                  serviceFeeAmount={payment.service_fee}
                  baseAmount={payment.amount_provider}
                />
                <PurseAllocationForm
                  allowedPurseIds={purses
                    .filter((purse) => {
                      return payment.allowed_students?.includes(
                        purse.student_id.toString()
                      )
                    })
                    .map((purse) => purse.id)}
                  paymentType={payment.type}
                  apiPurses={purses}
                  methods={acceptFormMethods.methods}
                  paymentAmount={totalApplicantAmount}
                />
                <PurseAllocationSummary
                  selectedPurses={acceptFormMethods.getSelectedPurses()}
                  total={totalApplicantAmount}
                />

                <Button
                  onClick={acceptFormMethods.methods.handleSubmit(
                    handleAcceptPayment
                  )}
                >
                  Accept Payment
                </Button>
              </DialogContent>
            </Dialog>
            <Dialog
              open={isRejectDialogOpen}
              onOpenChange={setIsRejectDialogOpen}
            >
              <DialogTrigger asChild>
                <Button className="items-center" variant={'outline'}>
                  <FailCircleIcon className="text-red-600 mr-1" />
                  {t('decline_payment')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{t('decline_payment')}</DialogTitle>
                  <DialogDescription>
                    {t('provide_rejection_reason')}
                  </DialogDescription>
                </DialogHeader>
                <form
                  onSubmit={rejectFormMethods.handleSubmit(handleRejectPayment)}
                >
                  <div className="grid gap-4 py-4">
                    <Controller
                      control={rejectFormMethods.control}
                      name="comment"
                      render={({ field, fieldState }) => {
                        return (
                          <div>
                            <Textarea
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              id="rejection-reason"
                              placeholder={t('rejection_reason')}
                              className="col-span-2 h-24"
                            />
                            <FormErrorMessage
                              message={fieldState.error?.message}
                            />
                          </div>
                        )
                      }}
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setIsRejectDialogOpen(false)}
                    >
                      {t('cancel')}
                    </Button>
                    <SubmitButton
                      buttonTxt={t('submit_rejection')}
                      status={rejectMutation.status}
                    />
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
